// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
//   - Brand wordmark (myvitae.ai) keeps Quicksand via logoFontStyle — don't touch.
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardBody,
  CardHeader,
  ChevronDownIcon,
  ChevronUpIcon,
  FormField,
  Input,
  PrimaryButton,
  SecondaryButton,
  Select,
  StatusBadge,
  Toggle,
} from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import { useAdminAutomationSettings, useUpdateAdminAutomationSettings } from "../../hooks/useAdminAutomationSettings";
import { HOUR_OPTIONS } from "./automationUtils";

const MAX_CAP = 1000;

const noticeStyles = {
  success: "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
  error: "bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
};

const CAP_FIELDS = [
  { key: "cap_last_7_days", label: "Last 7 days", window: "7-day" },
  { key: "cap_last_10_days", label: "Last 10 days", window: "10-day" },
  { key: "cap_last_30_days", label: "Last 30 days", window: "30-day" },
];

const pad2 = (n) => String(n).padStart(2, "0");

const settingsToForm = (settings) => ({
  cap_last_7_days: String(settings?.cap_last_7_days ?? ""),
  cap_last_10_days: String(settings?.cap_last_10_days ?? ""),
  cap_last_30_days: String(settings?.cap_last_30_days ?? ""),
  quiet_hours_enabled: Boolean(settings?.quiet_hours_enabled),
  quiet_hours_start_hour: String(settings?.quiet_hours_start_hour ?? "21"),
  quiet_hours_end_hour: String(settings?.quiet_hours_end_hour ?? "9"),
});

const validateCap = (value) => {
  const s = String(value ?? "").trim();
  if (!s) return "Enter a number (0 disables this cap).";
  const n = Number(s);
  if (!Number.isInteger(n)) return "Must be a whole number.";
  if (n < 0 || n > MAX_CAP) return `Must be between 0 and ${MAX_CAP}.`;
  return "";
};

const summarize = (settings) => {
  if (!settings) return "";
  const caps = CAP_FIELDS.map((f) => (Number(settings[f.key]) > 0 ? settings[f.key] : "off")).join(" / ");
  const quiet = settings.quiet_hours_enabled
    ? `quiet hours ${pad2(settings.quiet_hours_start_hour)}:00–${pad2(settings.quiet_hours_end_hour)}:00`
    : "quiet hours off";
  return `Caps ${caps} · ${quiet}`;
};

/**
 * Global delivery policy card (collapsible): rolling frequency caps for the
 * last 7 / 10 / 30 days (0 = disabled) and quiet hours (recipient-local).
 */
const AutomationSettingsCard = ({ defaultOpen = false, className = "" }) => {
  const [open, setOpen] = useState(defaultOpen);
  const { settings, isLoading, error, refetch } = useAdminAutomationSettings();
  const updateMutation = useUpdateAdminAutomationSettings();
  const [notice, setNotice] = useState(null);

  const initial = useMemo(() => settingsToForm(settings), [settings]);
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);

  const capErrors = useMemo(
    () => Object.fromEntries(CAP_FIELDS.map((f) => [f.key, validateCap(form[f.key])])),
    [form]
  );
  const hasErrors = Object.values(capErrors).some(Boolean);
  const isDirty = JSON.stringify(form) !== JSON.stringify(initial);
  const isDefault = Boolean(settings?.is_default);

  const onSave = async () => {
    if (hasErrors) return;
    setNotice(null);
    try {
      await updateMutation.mutateAsync({
        cap_last_7_days: Number(form.cap_last_7_days),
        cap_last_10_days: Number(form.cap_last_10_days),
        cap_last_30_days: Number(form.cap_last_30_days),
        quiet_hours_enabled: Boolean(form.quiet_hours_enabled),
        quiet_hours_start_hour: Number(form.quiet_hours_start_hour),
        quiet_hours_end_hour: Number(form.quiet_hours_end_hour),
      });
      setNotice({ type: "success", message: "Global delivery policy saved. It applies to the next delivery cycle." });
    } catch (e) {
      setNotice({ type: "error", message: e?.message || "Failed to save the delivery policy." });
    }
  };

  const subtitle = isLoading
    ? "Loading…"
    : error
      ? "Could not load the policy."
      : open
        ? "Rolling caps on how many automation messages a user may receive, and the quiet hours during which emails are held. Applies to every automation that does not opt out."
        : summarize(settings) || "Frequency caps and quiet hours for every automation.";

  return (
    <Card variant="glass" className={className}>
      <CardHeader
        title="Global delivery policy"
        subtitle={subtitle}
        actions={
          <div className="flex items-center gap-2">
            {!isLoading && isDefault ? (
              <StatusBadge size="sm" variant="info" title="Nothing saved yet — the built-in defaults apply">
                Using defaults
              </StatusBadge>
            ) : null}
            <SecondaryButton
              type="button"
              size="sm"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls="automation-settings-panel"
              rightIcon={open ? <ChevronUpIcon size="sm" /> : <ChevronDownIcon size="sm" />}
            >
              {open ? "Hide" : "Show"}
            </SecondaryButton>
          </div>
        }
      />

      {open ? (
        <CardBody id="automation-settings-panel" className="mt-4 space-y-5">
          {error ? (
            <div className={`rounded-2xl px-4 py-3 ${noticeStyles.error}`} role="alert">
              <p className={`${typography.bodySmall}`}>{error?.message || "Failed to load the delivery policy."}</p>
              <div className="mt-2">
                <SecondaryButton size="sm" onClick={() => refetch()}>
                  Retry
                </SecondaryButton>
              </div>
            </div>
          ) : null}

          {isLoading ? (
            <div className="animate-pulse space-y-3" aria-busy="true">
              <div className={`h-4 ${surfaces.tertiary} rounded w-1/3`} />
              <div className={`h-10 ${surfaces.tertiary} rounded w-full`} />
              <div className={`h-4 ${surfaces.tertiary} rounded w-1/4`} />
              <div className={`h-10 ${surfaces.tertiary} rounded w-2/3`} />
            </div>
          ) : (
            <>
              {isDefault ? (
                <div className="rounded-2xl px-4 py-3 bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800">
                  <p className={`${typography.bodySmall} text-blue-800 dark:text-blue-300`}>
                    No policy has been saved yet — the values below are the built-in defaults. Saving creates the first policy.
                  </p>
                </div>
              ) : null}

              <section aria-labelledby="automation-caps-title" className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}>
                <h3 id="automation-caps-title" className={`${typography.h5} ${textColors.primary}`}>Frequency caps</h3>
                <p className={`mt-1 mb-4 ${typography.caption} ${textColors.tertiary}`}>
                  Maximum automation messages one user may receive in each rolling window, across all automations.
                  When a cap binds, higher-priority automations win. 0 disables that window. Automations with
                  “Ignore global frequency caps” bypass all three.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {CAP_FIELDS.map((field) => (
                    <FormField key={field.key} label={field.label} htmlFor={`automation-${field.key}`}>
                      <Input
                        id={`automation-${field.key}`}
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={MAX_CAP}
                        step={1}
                        value={form[field.key]}
                        onChange={(e) => setForm((p) => ({ ...p, [field.key]: e.target.value }))}
                        error={capErrors[field.key]}
                        helperText={
                          Number(form[field.key]) === 0 && !capErrors[field.key]
                            ? `${field.window} cap disabled.`
                            : `Max messages per user in the last ${field.label.replace("Last ", "")}.`
                        }
                        disabled={updateMutation.isPending}
                      />
                    </FormField>
                  ))}
                </div>
              </section>

              <section aria-labelledby="automation-quiet-title" className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}>
                <h3 id="automation-quiet-title" className={`${typography.h5} ${textColors.primary}`}>Quiet hours</h3>
                <p className={`mt-1 mb-4 ${typography.caption} ${textColors.tertiary}`}>
                  Emails scheduled inside this window are held until it ends, in the recipient's local time zone.
                  Inbox messages are never delayed. Automations can opt out with “Respect quiet hours” off.
                </p>
                <Toggle
                  id="automation-quiet-hours-enabled"
                  checked={Boolean(form.quiet_hours_enabled)}
                  onChange={(e) => setForm((p) => ({ ...p, quiet_hours_enabled: Boolean(e?.target?.checked) }))}
                  label={form.quiet_hours_enabled ? "Quiet hours enabled" : "Quiet hours disabled"}
                  labelPosition="right"
                  disabled={updateMutation.isPending}
                />
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
                  <FormField label="Start (recipient-local)" htmlFor="automation-quiet-start">
                    <Select
                      id="automation-quiet-start"
                      value={form.quiet_hours_start_hour}
                      onChange={(e) => setForm((p) => ({ ...p, quiet_hours_start_hour: e.target.value }))}
                      options={HOUR_OPTIONS}
                      placeholder={null}
                      disabled={updateMutation.isPending || !form.quiet_hours_enabled}
                    />
                  </FormField>
                  <FormField label="End (recipient-local)" htmlFor="automation-quiet-end">
                    <Select
                      id="automation-quiet-end"
                      value={form.quiet_hours_end_hour}
                      onChange={(e) => setForm((p) => ({ ...p, quiet_hours_end_hour: e.target.value }))}
                      options={HOUR_OPTIONS}
                      placeholder={null}
                      disabled={updateMutation.isPending || !form.quiet_hours_enabled}
                    />
                  </FormField>
                </div>
                {form.quiet_hours_enabled ? (
                  <p className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>
                    Emails are held between {pad2(form.quiet_hours_start_hour)}:00 and {pad2(form.quiet_hours_end_hour)}:00
                    {Number(form.quiet_hours_start_hour) > Number(form.quiet_hours_end_hour) ? " (overnight)" : ""}.
                  </p>
                ) : null}
              </section>

              {notice ? (
                <div className={`rounded-2xl px-4 py-3 ${noticeStyles[notice.type]}`} role="status">
                  <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{notice.message}</p>
                </div>
              ) : null}

              <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
                <SecondaryButton
                  onClick={() => {
                    setForm(initial);
                    setNotice(null);
                  }}
                  disabled={!isDirty || updateMutation.isPending}
                >
                  Reset
                </SecondaryButton>
                <PrimaryButton onClick={onSave} disabled={!isDirty || hasErrors} loading={updateMutation.isPending}>
                  Save policy
                </PrimaryButton>
              </div>
            </>
          )}
        </CardBody>
      ) : null}
    </Card>
  );
};

AutomationSettingsCard.propTypes = {
  defaultOpen: PropTypes.bool,
  className: PropTypes.string,
};

export default AutomationSettingsCard;
